import { cx } from '@linaria/core';
import { memo, useState, useMemo } from 'react';
import { NotificationBadge } from '@pm2/react-utils';
import { useNotifier } from '@provider/notifier';
import { breakpoints } from '@ui/css';
import { Row, Table, Cell } from '@ui/kit';
import { formatTimestamp } from '@utils/common';
import { useMatchBreakpoint, SizeTransition, Chevron } from '@utils/react';
import { deepCompare } from '../../../utils/deep-compare.js';
import { EntriesDialogIcon } from '../atoms/icon.dialog.js';
import { makePreviewModalConfig } from '../molecules/modal.preview.js';
import { tableModifiers } from '../shared.table.style.js';
import { useComponentEntrySelector } from '../state.js';
import { rowSizingCls, item, modifiers, transitionDurationMs, nestedContent, mobileRowHeading, mobileCell, mobileActionsContainer, mobileDialogCell, showAnswersButton } from './table.item.style.js';
import { NestedItem } from './table.nested-item.js';
import { NestedTHead } from './table.nested-thead.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { isNotificationConsideredRecent } from '../../notifications/util.is-considered-recent.js';
import { useDeleteNotificationMutation } from '../../notifications/api.delete-notification.mutation.js';
import { useSortState, sortComponentEntries } from '../utils/sort.js';
import { filterComponentEntry } from '../utils/filter.js';

const Item = /*#__PURE__*/memo(function Item(props) {
  const isMobile = useMatchBreakpoint(breakpoints.mobile, {
    immediate: true
  });
  const deleteNotification = useDeleteNotificationMutation();
  const notifier = useNotifier();
  const filters = useComponentEntrySelector(state => state);
  const notificationCount = props.item.groupedEntries.filter(it => it.shareNotifications?.some(notifcation => isNotificationConsideredRecent(notifcation))).length;
  const hasDialogueNotificationsWithin = props.item.groupedEntries.some(it => it.dialogue.notifications.length > 0);

  // We need to mark all notifications entries related to the current
  // component as read when it is being opened. Unfortunatly we need to ensure
  // that all entries remain bolded until the user actually has interacted
  // with them.
  //
  // As such we keep an ephimeral state that stores which entries that
  // actually have been interacted with.
  const [unreadEntrySet, setUnreadEntrySet] = useState(new Set(props.item.groupedEntries.filter(it => it.shareNotifications?.some(notifcation => isNotificationConsideredRecent(notifcation))).map(it => it.key)));
  const [nestedSort, onSort] = useSortState();
  const nestedEntries = useMemo(() => props.item.groupedEntries.filter(it => filterComponentEntry(filters, it)).sort((a, b) => sortComponentEntries(nestedSort, a, b)).map(it => ({
    ...it,
    highlighted: unreadEntrySet.has(it.key)
  })), [props.item.groupedEntries, filters, nestedSort, unreadEntrySet]);
  const additionalProps = useMemo(() => ({
    withStats: props.withStats,
    onClick: itemIndex => {
      const entry = nestedEntries[itemIndex];
      if (entry) {
        setUnreadEntrySet(prev => {
          const next = new Set(prev);
          next.delete(entry.key);
          return next;
        });
      }
      notifier.modal(makePreviewModalConfig({
        entries: nestedEntries,
        initialIndex: itemIndex,
        onNavigated: index => {
          const navigatedTo = nestedEntries[index];
          if (navigatedTo) {
            setUnreadEntrySet(prev => {
              const next = new Set(prev);
              next.delete(navigatedTo.key);
              return next;
            });
          }
        }
      }));
    }
  }), [nestedEntries, notifier, props.withStats]);
  const onRowClick = () => {
    // Once a component is opened then ensure that all notification entries
    // related to that component is removed
    const toDelete = props.item.groupedEntries.flatMap(it => it.shareNotifications).filter(it => !!it);
    deleteNotification.mutate(toDelete);
    setTimeout(() => props.onClick(props.item.key), 25);
  };
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(Row, {
      className: cx(item, props.item.isExpanded && modifiers.expanded.className, notificationCount && tableModifiers.new.className),
      sizingCls: rowSizingCls,
      onClick: onRowClick,
      children: isMobile ? /*#__PURE__*/jsx(MobileRow, {
        ...props,
        hasDialogueNotificationsWithin: hasDialogueNotificationsWithin,
        notificationCount: notificationCount
      }) : /*#__PURE__*/jsx(DesktopRow, {
        ...props,
        notificationCount: notificationCount,
        hasDialogueNotificationsWithin: hasDialogueNotificationsWithin
      })
    }), /*#__PURE__*/jsx(SizeTransition, {
      in: props.item.isExpanded,
      mountOnEnter: true,
      unmountOnExit: true,
      axis: "vertical",
      classNames: nestedContent,
      timeout: transitionDurationMs,
      retainElementPositionDuringAnimation: true,
      children: ref => /*#__PURE__*/jsx("div", {
        ref: ref,
        className: nestedContent,
        children: /*#__PURE__*/jsx(Table, {
          contentKey: `${nestedSort.sortBy}-${nestedSort.direction}`,
          emptyMessage: undefined,
          thead: /*#__PURE__*/jsx(NestedTHead, {
            withStats: props.withStats,
            options: nestedSort,
            onSort: onSort
          }),
          items: nestedEntries,
          Item: NestedItem,
          additionalProps: additionalProps
        })
      })
    })]
  });
}, deepCompare);
function DesktopRow(props) {
  // We know that, in order for to create CoursesGroupedByComponent at least one
  // entry has to exist in the groupedEntries array.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const context = props.item.groupedEntries[0];

  // The overall item should display the latest date in which the given
  // component has received entries.
  const date = new Date(Math.max(...props.item.groupedEntries.map(it => Math.max(it.createdAt.getTime(), it.modifiedAt?.getTime() ?? 0))));
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(Cell, {
      role: "rowheader",
      children: context.component.title
    }), /*#__PURE__*/jsx(Cell, {
      children: context.product?.title
    }), /*#__PURE__*/jsx(Cell, {
      children: props.notificationCount ? /*#__PURE__*/jsx(NotificationBadge, {
        count: props.notificationCount,
        children: "Nyt"
      }) : formatTimestamp(date)
    }), /*#__PURE__*/jsx(Cell, {
      tooltip: false,
      children: /*#__PURE__*/jsxs("div", {
        className: showAnswersButton,
        children: [/*#__PURE__*/jsxs("span", {
          children: [props.item.groupedEntries.length, " svar"]
        }), /*#__PURE__*/jsx(Chevron, {
          size: 8,
          lineWidth: 1.5,
          vertical: true,
          inversed: props.item.isExpanded
        })]
      })
    }), /*#__PURE__*/jsx(Cell, {
      tooltip: false,
      "aria-label": props.hasDialogueNotificationsWithin ? "Der er nye dialoger i denne opgave" : "Der er ingen nye dialoger for denne opgave",
      children: /*#__PURE__*/jsx(EntriesDialogIcon, {
        hidden: !props.hasDialogueNotificationsWithin,
        hasNewNotifications: true,
        height: "19"
      })
    })]
  });
}
function MobileRow(props) {
  // We know that, in order for to create CoursesGroupedByComponent at least one
  // entry has to exist in the groupedEntries array.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const context = props.item.groupedEntries[0];
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsxs("div", {
      children: [/*#__PURE__*/jsx(Cell, {
        role: "rowheader",
        className: mobileRowHeading,
        children: context.component.title
      }), /*#__PURE__*/jsx(Cell, {
        className: mobileCell,
        children: context.product?.title
      })]
    }), /*#__PURE__*/jsx(Cell, {
      className: cx(mobileCell, mobileActionsContainer),
      children: props.notificationCount !== 0 && /*#__PURE__*/jsx(NotificationBadge, {
        count: props.notificationCount,
        children: "Nyt"
      })
    }), /*#__PURE__*/jsx(Cell, {
      className: cx(mobileCell, mobileDialogCell),
      tooltip: false,
      "aria-label": props.hasDialogueNotificationsWithin ? "Der er nye dialoger i denne opgave" : "Der er ingen nye dialoger for denne opgave",
      children: /*#__PURE__*/jsx(EntriesDialogIcon, {
        hidden: !props.hasDialogueNotificationsWithin,
        hasNewNotifications: true,
        height: "16"
      })
    }), /*#__PURE__*/jsx(Cell, {
      className: mobileCell,
      tooltip: false,
      children: /*#__PURE__*/jsxs("div", {
        className: showAnswersButton,
        children: [/*#__PURE__*/jsx(Chevron, {
          size: 10,
          lineWidth: 1.5,
          vertical: true,
          inversed: props.item.isExpanded
        }), /*#__PURE__*/jsxs("span", {
          children: [props.item.groupedEntries.length, " svar"]
        })]
      })
    })]
  });
}

export { Item };
//# sourceMappingURL=table.item.js.map
